import { useEffect } from 'react';

/**
 * Shared flusher plumbing for the offline queues (see `pendingQueue.ts`).
 *
 * `useQueueFlusher` owns the lifecycle every flusher had copy-pasted:
 * flush on mount, background retry interval, online/focus re-triggers,
 * offline skip, and a cancelled flag so an in-flight flush aborts cleanly
 * when the effect tears down. The consumer supplies only the drain logic.
 *
 * The policy constants (backoff, retention, grace) are shared here too so
 * the queues can't drift apart again — the lesson queue's MAX_ATTEMPTS
 * give-up is deliberately NOT here; it stays a lesson-only rule (see
 * PendingQueueFlusher).
 */

/** Background retry cadence shared by every offline-queue flusher. */
export const RETRY_INTERVAL_MS = 20_000;

/** Exponential backoff between attempts per entry, capped at 10 minutes. */
export const backoffMs = (attempts: number) => Math.min(RETRY_INTERVAL_MS * 2 ** attempts, 600_000);

/** Entries older than this are junk (a completion is useless months late). */
export const RETENTION_MS = 60 * 86_400_000;

/** Fresh queue-first sessions get a grace window so the direct mutation wins the race. */
export const SESSION_GRACE_MS = 30_000;

/** True once an entry's backoff window has elapsed (or it was never attempted). */
export const isEligible = (e: { attempts?: number; lastAttemptAt?: number }, now: number) =>
  !e.lastAttemptAt || now - e.lastAttemptAt >= backoffMs(e.attempts ?? 0);

export interface FlushController {
  /** Check after every await — the effect that started this flush tore down. */
  cancelled: () => boolean;
}

/**
 * Drain entries one at a time. On success `onSent` runs (remove + cache
 * writes), on failure `onFailed` (markAttempted) and the loop moves on —
 * the entry stays queued for the next tick. Stops dead when cancelled.
 */
export async function drainEntries<T, R>(opts: {
  entries: readonly T[];
  send: (entry: T) => Promise<R>;
  onSent: (entry: T, result: R) => void;
  onFailed: (entry: T) => void;
  cancelled: () => boolean;
}): Promise<void> {
  for (const entry of opts.entries) {
    try {
      const result = await opts.send(entry);
      if (opts.cancelled()) return;
      opts.onSent(entry, result);
    } catch {
      if (opts.cancelled()) return;
      opts.onFailed(entry);
      // Leave for the next tick.
    }
  }
}

/**
 * Mount-once flusher lifecycle. Pass `null` while there is no authed user.
 *
 * `flush` must be referentially stable per input set (wrap in useCallback
 * with the queue arrays in its deps) — a store update re-creates it, which
 * re-runs the effect and flushes immediately. That immediate re-flush is
 * load-bearing: it is what retries a failed entry without waiting for the
 * 20s tick, and why per-entry backoff gating exists (a hot loop otherwise).
 */
export function useQueueFlusher(flush: ((ctl: FlushController) => Promise<void>) | null): void {
  useEffect(() => {
    if (!flush) return;
    let cancelled = false;
    const ctl: FlushController = { cancelled: () => cancelled };

    const run = () => {
      if (cancelled) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      void flush(ctl);
    };

    // Flush on mount if anything is queued.
    run();

    const interval = window.setInterval(run, RETRY_INTERVAL_MS);
    window.addEventListener('online', run);
    window.addEventListener('focus', run);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener('online', run);
      window.removeEventListener('focus', run);
    };
  }, [flush]);
}
